import { useState } from "react";
import axios from "axios";
import { Star, PenLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-white border-black/10 text-[#1F1F1F] placeholder:text-[#9ca3af] focus-visible:ring-[#5A189A]";

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="review-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)} data-testid={`star-${n}`} className="p-0.5">
          <Star size={26} className={(hover || value) >= n ? "text-[#F4B400]" : "text-black/15"} fill={(hover || value) >= n ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

export function ReviewDialog({ open, setOpen, onSubmitted }) {
  const [form, setForm] = useState({ name: "", role: "", rating: 5, quote: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.quote) { toast.error("Please add your name and review."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you! Your review was sent for approval and will appear once approved.");
      setForm({ name: "", role: "", rating: 5, quote: "" });
      setOpen(false);
      onSubmitted?.();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white border-black/10 sm:max-w-[480px]" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-[#1F1F1F]">Share your experience</DialogTitle>
          <DialogDescription className="text-sm text-[#9ca3af]">Tell us about your shipment. Reviews appear once approved by our team.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input data-testid="review-name" placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <Input data-testid="review-role" placeholder="City / role (optional)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} />
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#4b5563]">Your rating</div>
            <StarPicker value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} />
          </div>
          <Textarea data-testid="review-quote" rows={4} placeholder="Tell us how we did *" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className={`${inputCls} resize-none`} />
          <button type="submit" data-testid="review-submit" disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#5A189A] hover:bg-[#3D0A6E] px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={16} />} Submit review
          </button>
          <p className="text-center text-xs text-[#9ca3af]">Reviews are checked by our team before they appear.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
